import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  DataSourceParams,
  EFilterSize,
  EFilterType,
  ETableColumnType,
  ITableColumn,
  ITableFIlterGroup,
} from '../../store/models/table.model';
import { IContactItem, IMoveContactUploadItem } from '../../store/models/contact.model';
import { ApiDataSource } from '../../datasources/api.datasrouce';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { SelectionChange } from '@angular/cdk/collections';
import { Button, ButtonColor, ButtonSize } from '../../store/models/button.model';
import { AccountService } from '../../store/services/account.service';
import { IAccount } from '../../store/models/account.model';
import { UserType } from '../../store/models/user.model';
import { ContactsService, IContactMoveResponse } from '../../store/services/contacts.service';
import { map, takeUntil } from 'rxjs/operators';
import { TableComponent } from '../../shared/ui/table/table.component';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openDialog, openSnackbar } from '../../store/actions/page.action';
import { BatchMoveDialogComponent } from './batch-move-dialog/batch-move-dialog.component';
import { EUploadState, UploadDialogComponent } from './upload-dialog/upload-dialog.component';
import { FileUploaderService } from '../../store/services/file-uploader.service';
import { UploadConfirmationDialogComponent } from './upload-confirmation-dialog/upload-confirmation-dialog.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { EventService } from '../../store/services/event.service';
import { Subject } from 'rxjs';

export const MOVE_CONTACTS_EVENT = 'move_contacts_finished_event';

@Component({
  selector: 'app-move-contacts',
  templateUrl: './move-contacts.component.html',
  styleUrls: ['./move-contacts.component.scss'],
})
export class MoveContactsComponent implements OnInit, OnDestroy {
  @ViewChild('tableComponent') tableComponent: TableComponent;
  showUploadingWarning: boolean;

  tableColumns: ITableColumn<IContactItem>[] = [
    {
      id: 'name',
      title: 'Name',
      renderer: (contact: IContactItem) =>
        `${contact.contact_firstname} ${contact.contact_lastname}`,
    },
    {
      id: 'contact_email',
      title: 'Email',
    },
    {
      id: 'account_name',
      title: 'Account',
      renderer: (contact: IContactItem) =>
        `${contact.account_firstname} ${contact.account_lastname}`,
    },
  ];

  uploadButtonProperties: Button = {
    size: ButtonSize.SMALL,
    color: ButtonColor.BLUE,
    text: 'Upload',
    onClick: () =>
      this.store$.dispatch(
        openDialog({
          dialogData: {
            content: UploadDialogComponent,
            componentData: {
              state: EUploadState.SELECT_TEMPLATE,
            },
            dialogWidth: '700px',
            title: 'Select Upload Template',
            canClose: true,
            buttons: [
              {
                text: 'Next',
                size: ButtonSize.NORMAL,
                color: ButtonColor.BLUE,
                onClick: dialog => {
                  if (!dialog.dialogForm.valid) {
                    dialog.dialogForm.markAllAsTouched();
                    return;
                  }

                  const selectedTemplate =
                    dialog.dialogForm.get('template').value;
                  dialog.closeDialog();
                  this.fileUploaderService.init({
                    accept: ['text/csv', '.xls', '.xlsx'],
                    buttonLabel: 'Select Files',
                    dialogTitle: 'Upload file',
                    requestUri:
                      environment.apiUrl + '/v1/contacts/upload-move',
                    showDropZone: true,
                    allowMultiple: false,
                    param: 'contacts_file',
                    maxFiles: 1,
                    maxFileSize: '4MB',
                    customWarning: 'Warning: If the destination account has the "Check user email domain" option enabled, an extra validation will be performed, that prevents contacts with the same domain as the user\'s email address from being added. Some contacts will not change ownership.',
                    extraParams: {
                      template: selectedTemplate,
                    },
                    onInit: () => {
                      this.showUploadingWarning = true;
                    },
                    onComplete: (response: IMoveContactUploadItem) => {
                      this.store$.dispatch(
                        openDialog({
                          dialogData: {
                            content: UploadConfirmationDialogComponent,
                            componentData: {
                              items: response,
                            },
                            dialogWidth: '900px',
                            title: 'Upload Status',
                            canClose: true,
                            onClose: () => this.showUploadingWarning = false
                          },
                        }),
                      );
                    },
                    onError: res =>
                      this.store$.dispatch(
                        openSnackbar({
                          snackbarData: {
                            data: {
                              message: res.error,
                              type: 'error',
                            },
                          },
                        }),
                      ),
                  });
                  this.fileUploaderService.selectFile();
                },
              },
            ],
          },
        }),
      ),
  };

  showTable: boolean;

  dataSource: ApiDataSource<IContactItem>;

  tableDataSourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'id',
    sortDir: 'asc',
    filters: {},
  };

  tableFilterGroups: ITableFIlterGroup[] = [
    {
      filters: [
        {
          type: EFilterType.SEARCH,
          size: EFilterSize.HALF,
          items: [
            {
              id: 'search',
              label: 'Search',
              icon: 'search',
            },
          ],
          onFilterChange: value => {
            this.searchChange(value);
          },
        },
      ],
    },
  ];

  accounts: IAccount[] = [];

  showActions: boolean;

  selectedContacts: IContactItem[] = [];

  tableReady: boolean;

  private multipleContactMoveResponse: IContactMoveResponse;

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly http: HttpClient,
    private readonly accountService: AccountService,
    private readonly contactsService: ContactsService,
    private readonly store$: Store<IReducers>,
    private readonly fileUploaderService: FileUploaderService,
    private readonly eventService: EventService
  ) {
    this.dataSource = new ApiDataSource<IContactItem>(
      this.http,
      environment.apiUrl + '/v1/contacts',
    );
  }

  searchChange(value) {
    this.tableDataSourceParams.filters.search = value;
  }

  ngOnInit(): void {
    this.eventService.on(MOVE_CONTACTS_EVENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.showUploadingWarning = false);
    this.accountService
      .getAccountsBasicInfo(UserType.SINGLE_USER)
      .subscribe(result => {
        this.accounts = result.items;
        this.tableColumns.push({
          id: 'account_id',
          title: 'Destination Account',
          type: ETableColumnType.SELECT,
          selectProperties: {
            type: 'select',
            label: 'Destination Account',
            items: this.accounts.map(account => ({
              label: `${account.first_name} ${account.last_name}`,
              value: account.account_id,
            })),
            onSelection: (value, tableComponent, element) => {
              const data = [
                ...this.dataSource.data.map(contact => {
                  if (contact.contact_id === element.contact_id) {
                    contact.account_id = value;
                    contact.group_id = null;
                  }

                  return contact;
                }),
              ];

              this.dataSource.internalRefresh(data);
            },
          },
        });

        this.tableColumns.push({
          id: 'group_id',
          title: 'Destination Group',
          type: ETableColumnType.SELECT,
          selectProperties: {
            type: 'select',
            label: 'Destination Group',
            asyncBuildItems: true,
            trackBy: 'account_id',
            buildItems: (row: IContactItem) => {
              return this.contactsService.getContactGroups(row.account_id).pipe(
                map(groups =>
                  groups.map(g => ({
                    label: g.group_name,
                    value: g.group_id,
                  })),
                ),
              );
            },
            onSelection: (value, tableComponent, element) => {
              const data = [
                ...this.dataSource.data.map(contact => {
                  if (contact.contact_id === element.contact_id) {
                    contact.group_id = value;
                  }

                  return contact;
                }),
              ];

              this.dataSource.internalRefresh(data);
            },
          },
        });

        this.tableColumns.push({
          id: 'status',
          title: 'Status',
          renderer: (contact: IContactItem) => {
            if (!this.multipleContactMoveResponse) {
              return '';
            }

            const movedItems = Array.isArray(this.multipleContactMoveResponse.moved)
              ? this.multipleContactMoveResponse.moved
              : [];
            const taragetMoved = movedItems.filter(
              m => m.contact_id === contact.contact_id,
            );

            if (taragetMoved && taragetMoved.length === 1) {
              return taragetMoved[0].message;
            }

            const failedItems = Array.isArray(this.multipleContactMoveResponse.failed)
              ? this.multipleContactMoveResponse.failed
              : [];
            const targetFailed = failedItems.filter(
              m => m.contact_id === contact.contact_id,
            );

            if (targetFailed && targetFailed.length === 1) {
              return targetFailed[0].message;
            }
          },
        });

        this.tableReady = true;
      });
  }

  onSelection($event: SelectionChange<IContactItem>) {
    this.showActions = $event.source.selected.length > 0;
    this.selectedContacts = $event.source.selected;

    if (this.showActions) {
      if (this.tableFilterGroups[0].filters.length === 2) {
        this.tableFilterGroups[0].filters.push({
          type: EFilterType.BUTTON,
          label: 'Create new',

          buttonProperties: {
            size: ButtonSize.SMALL,
            color: ButtonColor.ORANGE,
            text: 'Move',
            onClick: () =>
              this.store$.dispatch(
                openDialog({
                  dialogData: {
                    content: 'Are you sure you want to move selected contacts?',
                    dialogWidth: '500px',
                    title: 'Move Contacts',
                    canClose: true,
                    buttons: [
                      {
                        text: 'Move',
                        size: ButtonSize.NORMAL,
                        color: ButtonColor.BLUE,
                        onClick: dialog => {
                          const wrongSelected = this.selectedContacts.filter(
                            c => c.account_id === null,
                          );

                          if (wrongSelected && wrongSelected.length) {
                            alert(
                              'Please select destination account and group for all contacts',
                            );
                            return;
                          }

                          const paramsMap: {
                            contact_id: number[];
                            account_id: number[];
                            group_id: number[];
                          } = {
                            contact_id: [],
                            account_id: [],
                            group_id: [],
                          };

                          this.selectedContacts.forEach(c => {
                            paramsMap.contact_id.push(c.contact_id);
                            paramsMap.account_id.push(c.account_id);
                            // group_id is optional
                            if (c.group_id) {
                              paramsMap.group_id.push(c.group_id);
                            }
                          });

                          if (
                            (paramsMap.contact_id.length +
                              paramsMap.account_id.length) /
                            2 !==
                            paramsMap.contact_id.length
                          ) {
                            alert(
                              'Please select destination account and group for all contacts',
                            );
                            return;
                          }

                          this.contactsService
                            .moveMultipleContacts(paramsMap)
                            .subscribe(
                              result => {
                                this.multipleContactMoveResponse = result;
                                this.dataSource.refresh();
                                this.store$.dispatch(
                                  openSnackbar({
                                    snackbarData: {
                                      data: {
                                        message: this.getMoveResultMessage(result),
                                        type: 'success',
                                      },
                                    },
                                  }),
                                );
                                dialog.closeDialog();
                              },
                              error => this.handleMoveError(error, options =>
                                this.contactsService
                                  .moveMultipleContacts(paramsMap, options)
                                  .subscribe(
                                    retryResult => {
                                      this.multipleContactMoveResponse = retryResult;
                                      this.dataSource.refresh();
                                      this.store$.dispatch(
                                        openSnackbar({
                                          snackbarData: {
                                            data: {
                                              message: this.getMoveResultMessage(retryResult),
                                              type: 'success',
                                            },
                                          },
                                        }),
                                      );
                                      dialog.closeDialog();
                                    },
                                    retryError =>
                                      this.store$.dispatch(
                                        openSnackbar({
                                          snackbarData: {
                                            data: {
                                              message: retryError.error,
                                              type: 'error',
                                            },
                                          },
                                        }),
                                      ),
                                  ),
                              ),
                            );
                        },
                      },
                    ],
                  },
                }),
              ),
          },
        });
        this.tableFilterGroups[0].filters.push({
          type: EFilterType.BUTTON,
          label: 'Create new',

          buttonProperties: {
            size: ButtonSize.SMALL,
            color: ButtonColor.BLUE,
            text: 'Batch Move',
            onClick: (row: IContactItem) =>
              this.store$.dispatch(
                openDialog({
                  dialogData: {
                    content: BatchMoveDialogComponent,
                    componentData: {
                      accounts: this.accounts,
                    },
                    canClose: true,
                    dialogWidth: '700px',
                    title: 'Batch Move Contacts',
                    buttons: [
                      {
                        text: 'Move',
                        size: ButtonSize.NORMAL,
                        color: ButtonColor.BLUE,
                        onClick: dialog => {
                          if (!dialog.dialogForm.valid) {
                            dialog.dialogForm.markAllAsTouched();
                            return;
                          }

                          const selectedContactIds = this.selectedContacts.map(
                            c => c.contact_id,
                          );
                          const targetAccountId =
                            dialog.dialogForm.get('accountId').value;
                          const targetGroupId =
                            dialog.dialogForm.get('groupId').value;

                          this.contactsService
                            .moveMultipleContacts({
                              contact_id: selectedContactIds,
                              account_id: [targetAccountId],
                              group_id: [targetGroupId],
                            })
                            .subscribe(
                              result => {
                                this.multipleContactMoveResponse = result;
                                this.dataSource.refresh();
                                dialog.closeDialog();
                                this.store$.dispatch(
                                  openSnackbar({
                                    snackbarData: {
                                      data: {
                                        message: this.getMoveResultMessage(result),
                                        type: 'success',
                                      },
                                    },
                                  }),
                                );
                              },
                              error => this.handleMoveError(error, options =>
                                this.contactsService
                                  .moveMultipleContacts({
                                    contact_id: selectedContactIds,
                                    account_id: [targetAccountId],
                                    group_id: [targetGroupId],
                                  }, options)
                                  .subscribe(
                                    retryResult => {
                                      this.multipleContactMoveResponse = retryResult;
                                      this.dataSource.refresh();
                                      dialog.closeDialog();
                                      this.store$.dispatch(
                                        openSnackbar({
                                          snackbarData: {
                                            data: {
                                              message: this.getMoveResultMessage(retryResult),
                                              type: 'success',
                                            },
                                          },
                                        }),
                                      );
                                    },
                                    retryError =>
                                      this.store$.dispatch(
                                        openSnackbar({
                                          snackbarData: {
                                            data: {
                                              message: retryError.error,
                                              type: 'error',
                                            },
                                          },
                                        }),
                                      ),
                                  ),
                              ),
                            );
                        },
                      },
                    ],
                  },
                }),
              ),
          },
        });
      }
    } else {
      this.tableFilterGroups[0].filters.splice(2, 2);
    }
  }

  toggleTable($event: MatSlideToggleChange) {
    this.showTable = $event.checked;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  private getMoveResultMessage(result: IContactMoveResponse): string {
    const skippedCount = result?.skipped_same_domain_count || 0;
    if (skippedCount > 0) {
      return `Contacts move completed. ${skippedCount} contact(s) were skipped due to same domain policy.`;
    }
    return 'Contacts moved successfully. Please, check the status column for more details.';
  }

  private handleMoveError(
    error: any,
    onDecision: (options: { skip_same_domain_check?: boolean }) => void,
  ): void {
    let message = Array.isArray(error?.error?.message) ? error.error.message[0] : error?.error?.message;
    if (typeof message === 'string' && message.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(message);
        const firstKey = Object.keys(parsed || {})[0];
        const parsedMessage = firstKey ? parsed[firstKey]?.[0] : null;
        if (typeof parsedMessage === 'string') {
          message = parsedMessage;
        }
      } catch (e) {
      }
    }
    const normalizedMessage = typeof message === 'string' ? message.toLowerCase() : '';
    const isSameDomainError = normalizedMessage.includes('same domain') || normalizedMessage.includes('matching domain');
    if (isSameDomainError) {
      this.store$.dispatch(openDialog({
        dialogData: {
          title: 'Check user email domain',
          content: message || "Email address of the contact has a matching domain with your account's email domain.<br/>"
            + 'You can either: Skip the validation and add the contact anyway, or accept the validation and do not create that contact.',
          canClose: false,
          onClose: () => {},
          buttons: [
            {
              text: 'DO NOT CREATE',
              color: ButtonColor.RED,
              size: ButtonSize.NORMAL,
              onClick: dialog => {
                dialog.closeDialog();
                this.store$.dispatch(
                  openSnackbar({
                    snackbarData: {
                      data: {
                        message: 'Contacts were not moved.',
                        type: 'error',
                      },
                    },
                  }),
                );
              },
            },
            {
              text: 'ADD ANYWAY',
              color: ButtonColor.BLUE,
              size: ButtonSize.NORMAL,
              onClick: dialog => {
                dialog.closeDialog();
                onDecision({ skip_same_domain_check: true });
              },
            },
          ],
        },
      }));
      return;
    }
    this.store$.dispatch(
      openSnackbar({
        snackbarData: {
          data: {
            message: error?.error || 'Unexpected error',
            type: 'error',
          },
        },
      }),
    );
  }
}
